import { useEffect, useMemo, useState } from 'react';

import TiptapCollabProvider from '@tiptap-pro/provider';
import Collaboration from '@tiptap/extension-collaboration';
import * as Y from 'yjs';

import { useTiptapTokenSuspense } from '../useTiptapTokenSuspense';

export type TiptapCollaborationAdapter = {
  doc: Y.Doc;
  collaborationExtension?: ReturnType<typeof Collaboration.configure>;
  token?: {
    tiptapDocumentName?: string | null;
    token: string;
    appId: string;
  } | null;
  error?: Error | string;
};

export const useTiptapCollaboration = ({
  documentName,
  enabled = true,
}: {
  documentName: string;
  enabled?: boolean;
}) => {
  const [provider, setProvider] = useState<TiptapCollabProvider | null>(null);
  const [providerError, setProviderError] = useState<Error | null>(null);

  const tokenResult = useTiptapTokenSuspense({
    name: documentName,
    skip: !enabled,
  });

  const doc = useMemo(() => new Y.Doc(), []);

  const collaborationExtension = useMemo(
    () =>
      enabled
        ? Collaboration.configure({
            document: doc,
            field: 'default',
          })
        : undefined,
    [doc, enabled],
  );

  const collaboration: TiptapCollaborationAdapter = {
    doc,
    collaborationExtension,
    token: tokenResult.getTiptapToken,
  };

  useEffect(() => {
    setProviderError(null);
    setProvider(null);
  }, [documentName, enabled]);

  useEffect(() => {
    if (!enabled || !collaboration.token || provider) {
      return;
    }

    try {
      const collabProvider = new TiptapCollabProvider({
        name: collaboration.token.tiptapDocumentName || '',
        document: doc,
        token: collaboration.token.token,
        appId: collaboration.token.appId,
        onAuthenticationFailed: () => {
          setProviderError(
            new Error('Authentication failed for collaboration provider'),
          );
        },
        onSynced: () => {
          setProviderError(null);
        },
      });

      setProvider(collabProvider);
    } catch (err) {
      setProviderError(
        err instanceof Error
          ? err
          : new Error('Failed to initialize collaboration provider'),
      );
    }
  }, [collaboration.token, provider, doc, enabled]);

  useEffect(() => {
    return () => {
      provider?.destroy();
    };
  }, [provider]);

  useEffect(() => {
    return () => {
      doc.destroy();
    };
  }, [doc]);

  return {
    collaboration,
    providerError,
  };
};
